import type AppStore from "../stores/AppStore"
import { defaultUser, type IUser } from "../models/User"
import type AppApi from "./AppApi"
import {
  signInWithPopup,
  OAuthProvider,
  signOut,
  signInWithRedirect,
  type UserCredential,
  onAuthStateChanged,
  type User as FirebaseUser,
  browserSessionPersistence,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  type AuthError,
} from "firebase/auth"
import { auth, db } from "../config/firebase-config"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { MAIL_EMAIL, WELCOME_SUBJECT, WELCOME_MESSAGE } from "../functions/mailMessages"
import { logger } from "../functions/Logger"
import type { FirebaseError } from "firebase/app"

const GRAPH_API_ENDPOINTS = {
  ME: "https://graph.microsoft.com/beta/me", // get profile data https://graph.microsoft.com/beta/$metadata
}

// Type guard to check if error is a Firebase error
const isFirebaseError = (error: unknown): error is FirebaseError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    "message" in error &&
    typeof (error as any).code === "string"
  )
}

// Type guard to check if error is an Auth error
const isAuthError = (error: unknown): error is AuthError => {
  return isFirebaseError(error) && (error as any).code?.startsWith("auth/")
}

// Helper function to safely get error message
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  if (isFirebaseError(error)) {
    return error.message
  }
  if (typeof error === "string") {
    return error
  }
  return "An unknown error occurred"
}

// Helper function to safely get error code
const getErrorCode = (error: unknown): string | null => {
  if (isFirebaseError(error)) {
    return error.code
  }
  return null
}

export default class AuthApi {
  private provider = new OAuthProvider("microsoft.com")

  constructor(
    private api: AppApi,
    private store: AppStore,
  ) {
    this.provider.setCustomParameters({
      login_hint: "user@ecb.org.na",
      tenant: "7183259b-b22f-45f2-a259-cd6ab7231b66", // ecb
    })
    this.provider.addScope("mail.read")
    this.provider.addScope("calendars.read")
    this.handleAuthStateChange()
  }

  private handleAuthStateChange() {
    onAuthStateChanged(auth, async (user) => {
      this.store.auth.setLoading(true) // start loading.
      if (!user) {
        this.logOut()
        this.store.auth.setLoading(false) // stop loading.
        return
      }

      try {
        await this.handleUserBasicInfo(user)
      } catch (error) {
        logger.error("Error handling user credential", error)
        this.logOut()
      }
    })
  }

  async logInWithPopup() {
    try {
      await setPersistence(auth, browserSessionPersistence)
      const cred = await signInWithPopup(auth, this.provider)
      await this.handleUserCredential(cred)
    } catch (error) {
      logger.error("Error logging in with popup", error)
      throw error
    }
  }

  async logInWithRedirect() {
    try {
      await setPersistence(auth, browserSessionPersistence)
      signInWithRedirect(auth, this.provider)
    } catch (error) {
      logger.error("Error logging in with redirect", error)
      throw error
    }
  }

  private async handleUserCredential(result: UserCredential) {
    // User is signed in.
    // Get the OAuth access token and ID Token
    const credential = OAuthProvider.credentialFromResult(result)
    if (!credential) {
      this.logOut() // no user credentials, logOut user.
      return
    }

    // accessToken & idToken
    const { accessToken = "" } = credential
    // handle authenticated user
    const user = result.user
    // get user info
    const userInfo = await this.getUserInfo(accessToken)
    const additionalUserInfo = {
      email: userInfo.mail || userInfo.userPrincipalName,
      jobTitle: userInfo.jobTitle || "",
    }

    // handle user basic info
    await this.handleUserBasicInfo(user, additionalUserInfo)
  }

  private async handleUserBasicInfo(user: FirebaseUser, additionalUserInfo: any = {}) {
    try {
      const userDocRef = doc(db, "users", user.uid)
      const $doc = await getDoc(userDocRef)

      // Create user object from Firebase Auth data
      const userData: IUser = {
        ...defaultUser,
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        isAnonymous: user.isAnonymous,
        createdAt: user.metadata.creationTime || null,
        lastLoginAt: user.metadata.lastSignInTime || null,
        ...additionalUserInfo,
      }

      // If user doesn't exist in Firestore, create them
      if (!$doc.exists()) {
        logger.info("User not found in Firestore, creating new user document", {
          uid: user.uid,
          email: user.email,
        })

        try {
          // Create user document in Firestore "users" collection
          await setDoc(userDocRef, userData)

          // Load user into store
          this.store.user.load([userData])

          // Set as authenticated user
          this.store.auth.logIn(userData)

          // Send welcome email
          if (userData.email) {
            try {
              await this.api.mail.sendMail([userData.email], MAIL_EMAIL, WELCOME_SUBJECT, WELCOME_MESSAGE)
              logger.info("Welcome email sent successfully", { email: userData.email })
            } catch (emailError) {
              logger.error("Failed to send welcome email", emailError)
              // Don't fail the entire process if email fails
            }
          }

          logger.info("New user document created successfully in users collection", { uid: user.uid })
        } catch (createError) {
          logger.error("Error creating user document in Firestore", createError)
          // Still log them in even if database creation fails
          this.store.auth.logIn(userData)
          throw createError
        }
      } else {
        // User exists, merge existing data with current auth data
        const existingData = $doc.data() as IUser
        const updatedUser: IUser = {
          ...existingData,
          // Always update these fields from Firebase Auth
          displayName: user.displayName || existingData.displayName,
          email: user.email || existingData.email,
          phoneNumber: user.phoneNumber || existingData.phoneNumber,
          photoURL: user.photoURL || existingData.photoURL,
          emailVerified: user.emailVerified,
          lastLoginAt: user.metadata.lastSignInTime || null,
          ...additionalUserInfo,
        }

        try {
          // Update user document in Firestore
          await setDoc(userDocRef, updatedUser, { merge: true })

          // Load user into store
          this.store.user.load([updatedUser])

          // Set as authenticated user
          this.store.auth.logIn(updatedUser)

          logger.info("Existing user document updated successfully", { uid: user.uid })
        } catch (updateError) {
          logger.error("Error updating user document in Firestore", updateError)
          // Still log them in with existing data
          this.store.auth.logIn(updatedUser)
        }
      }

      this.store.auth.setLoading(false)
    } catch (error) {
      logger.error("Error handling user basic info", error)
      this.store.auth.setLoading(false)

      // Show user-friendly error message with proper type checking
      const errorCode = getErrorCode(error)
      const errorMessage = getErrorMessage(error)

      if (errorCode === "resource-exhausted") {
        window.alert("The database has reached the daily limit. Please try again later.")
      } else if (errorCode === "unavailable") {
        window.alert("The service is currently unavailable. Please check your internet connection and try again.")
      } else if (errorCode === "permission-denied") {
        window.alert("Access denied. Please contact your administrator.")
      } else if (errorCode === "unauthenticated") {
        window.alert("Authentication failed. Please sign in again.")
      } else {
        window.alert(`An error occurred while setting up your account: ${errorMessage}`)
      }

      throw error
    }
  }

  private async getUserInfo(accessToken: string) {
    try {
      const userInfo = await this.callGraphApi(GRAPH_API_ENDPOINTS.ME, accessToken)
      return userInfo
    } catch (error) {
      logger.error("Error getting user info from Graph API", error)
      // Return empty object if Graph API fails
      return {}
    }
  }

  private async callGraphApi(endpoint: string, token: string) {
    try {
      const headers = new Headers()
      const bearer = `Bearer ${token}`
      headers.append("Authorization", bearer)

      const options = {
        method: "GET",
        headers: headers,
      }

      const response = await fetch(endpoint, options)

      if (!response.ok) {
        throw new Error(`Graph API request failed: ${response.status} ${response.statusText}`)
      }

      const userInfo = await response.json()
      return userInfo
    } catch (error) {
      logger.error("Error calling Graph API", error)
      throw error
    }
  }

  // Sign in with email and password firebase
  async signIn(email: string, password: string): Promise<FirebaseUser | null> {
    try {
      // Set persistence first
      await setPersistence(auth, browserLocalPersistence)

      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(auth, email, password)

      if (userCredential?.user) {
        logger.info("User signed in successfully", { uid: userCredential.user.uid, email })
        // The onAuthStateChanged listener will handle user creation/update
        return userCredential.user
      }

      return null
    } catch (error) {
      logger.error("Error signing in with email and password", error)

      // Handle specific Firebase Auth errors with proper type checking
      let errorMessage = "Sign in failed. Please try again."

      if (isAuthError(error)) {
        switch (error.code) {
          case "auth/invalid-login-credentials":
          case "auth/user-not-found":
          case "auth/wrong-password":
            errorMessage = "Invalid email or password. Please check your credentials and try again."
            break
          case "auth/invalid-email":
            errorMessage = "Invalid email address format."
            break
          case "auth/user-disabled":
            errorMessage = "This account has been disabled. Please contact support."
            break
          case "auth/too-many-requests":
            errorMessage = "Too many failed attempts. Please try again later."
            break
          case "auth/network-request-failed":
            errorMessage = "Network error. Please check your internet connection."
            break
          case "auth/operation-not-allowed":
            errorMessage = "Email/password sign-in is not enabled. Please contact support."
            break
          case "auth/weak-password":
            errorMessage = "Password is too weak. Please choose a stronger password."
            break
          default:
            errorMessage = `Sign in failed: ${error.message}`
        }
      } else {
        // Handle non-Auth errors
        const genericMessage = getErrorMessage(error)
        errorMessage = `Sign in failed: ${genericMessage}`
      }

      // Show user-friendly error
      window.alert(errorMessage)
      throw new Error(errorMessage)
    }
  }

  // logout
  async logOut() {
    try {
      await signOut(auth)
      logger.info("User signed out successfully")
    } catch (error) {
      logger.error("Error signing out", error)
      const errorMessage = getErrorMessage(error)
      window.alert(`Sign-out failed: ${errorMessage}`)
    }

    // Remove user from store regardless of sign-out success
    this.store.auth.logOut()
  }
}
